/**
 * What a lookup tried: each try, and on the network each fetcher that ran, in order,
 * with what it got. Written by the server into `payload.fetch_log` (boardroom/lookups.py
 * `logged`), newest last, at most five tries.
 *
 * Two sizes: `FetchLogLine` is the latest try on one line, for a feed card, so a list
 * filtered to "failed" reads as a list of reasons; `FetchLog` is every try, for the drawer.
 */
import { feedTime, feedTimeTitle } from './feedTime';

export interface FetchAttempt {
  fetcher: string;
  /** Searches only: which engine this fetcher asked. */
  engine?: string;
  /** not_needed: an earlier fetcher answered. not_tried: it never ran (can't search that
   *  engine, or resting after repeated failures). */
  outcome: 'ok' | 'missing' | 'failed' | 'skipped' | 'not_needed' | 'not_tried';
  reason?: string;
  status?: number | null;
  seconds?: number;
  /** "4.3.2.1:8080", "direct", or absent for a browser. */
  proxy?: string | null;
  bytes?: number | null;
}

export interface FetchTry {
  at?: string;
  /** "network", "cache", "browser extension". */
  via: string;
  /** Absent while the try is still out (sent to the browser). */
  ok?: boolean;
  note?: string;
  attempts?: FetchAttempt[];
}

export function fetchLog(payload: Record<string, unknown>): FetchTry[] {
  const raw = payload.fetch_log;
  return Array.isArray(raw)
    ? (raw.filter(t => t && typeof t === 'object' && typeof (t as FetchTry).via === 'string') as FetchTry[])
    : [];
}

const MARK: Record<FetchAttempt['outcome'], { sym: string; cls: string; label: string }> = {
  ok: { sym: '✓', cls: 'text-emerald-700 dark:text-emerald-300', label: 'got it' },
  missing: { sym: '∅', cls: 'text-gray-600 dark:text-gray-300', label: 'not there' },
  failed: { sym: '✗', cls: 'text-red-600 dark:text-red-400', label: 'failed' },
  skipped: { sym: '–', cls: 'text-gray-400 dark:text-gray-500', label: "couldn't run" },
  not_needed: { sym: '·', cls: 'text-gray-400 dark:text-gray-500', label: 'not needed' },
  not_tried: { sym: '·', cls: 'text-gray-400 dark:text-gray-500', label: 'not tried' },
};

const RAN = (a: FetchAttempt) => a.outcome !== 'not_needed' && a.outcome !== 'not_tried';

function bytesLabel(n: number): string {
  return n < 10_240 ? `${n.toLocaleString()} bytes` : `${Math.round(n / 1024).toLocaleString()} KB`;
}

/** "HTTP 200 · 3,434 bytes · 4.2s" — what one step got, as far as it's known. */
function facts(a: FetchAttempt): string {
  return [
    a.status ? `HTTP ${a.status}` : null,
    a.bytes ? bytesLabel(a.bytes) : null,
    a.seconds ? `${a.seconds}s` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function who(a: FetchAttempt): string {
  return a.engine ? `${a.fetcher} on ${a.engine}` : a.fetcher;
}

function tryHeadline(t: FetchTry): string {
  if (t.via === 'network') return t.attempts?.length ? '' : 'No fetcher could run';
  return [t.via === 'cache' ? 'From the cache' : `Via the ${t.via}`, t.note].filter(Boolean).join(': ');
}

/** The latest try, on one line: "Plain HTTP ✗ ReadTimeout → crawl4ai ✓". */
export function FetchLogLine({ payload }: { payload: Record<string, unknown> }) {
  const log = fetchLog(payload);
  const last = log[log.length - 1];
  if (!last) return null;
  const head = tryHeadline(last);
  return (
    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2" data-testid="fetch-line">
      {log.length > 1 && <span className="mr-1">Try {log.length}:</span>}
      {head && <span>{head}</span>}
      {(last.attempts ?? []).filter(RAN).map((a, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1 text-gray-300 dark:text-gray-600">→</span>}
          {who(a)} <span className={MARK[a.outcome]?.cls}>{MARK[a.outcome]?.sym ?? '?'}</span>
          {a.outcome !== 'ok' && a.reason ? ` ${a.reason}` : ''}
        </span>
      ))}
    </p>
  );
}

/** Every try, newest first. Each is a numbered progression down the ladder: the fetcher,
 *  the proxy it went out through, what it got (status, bytes, time) and why; fetchers it
 *  never reached read as not needed. */
export default function FetchLog({ payload }: { payload: Record<string, unknown> }) {
  const log = fetchLog(payload);
  if (!log.length) return null;
  return (
    <section className="mt-3" data-testid="fetch-log">
      <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">What was tried</h4>
      <ol className="space-y-2">
        {log
          .map((t, i) => ({ t, n: i + 1 }))
          .reverse()
          .map(({ t, n }) => (
            <li key={n} className="text-xs">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                <span className="font-medium">Try {n}</span>
                {t.at && (
                  <span className="text-gray-400 tabular-nums" title={feedTimeTitle(t.at)}>
                    {feedTime(t.at)}
                  </span>
                )}
                <span
                  className={
                    t.ok === true
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : t.ok === false
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-amber-700 dark:text-amber-300'
                  }
                >
                  {t.ok === true ? 'worked' : t.ok === false ? 'failed' : 'waiting'}
                </span>
              </div>
              {tryHeadline(t) && <div className="text-gray-500 dark:text-gray-400">{tryHeadline(t)}</div>}
              {!!t.attempts?.length && (
                <ol className="mt-1 space-y-1" data-testid="fetch-steps">
                  {t.attempts.map((a, i) => (
                    <li
                      key={i}
                      className={`flex gap-2 ${RAN(a) ? '' : 'text-gray-400 dark:text-gray-500'}`}
                    >
                      <span className="w-4 shrink-0 text-right tabular-nums text-gray-400">{i + 1}.</span>
                      <div className="min-w-0">
                        <div>
                          <span className={RAN(a) ? 'font-medium text-gray-800 dark:text-gray-100' : ''}>
                            {who(a)}
                          </span>
                          {a.proxy && (
                            <span className="text-gray-400">
                              {' '}
                              {a.proxy === 'direct' ? '(direct, no proxy)' : `(via proxy ${a.proxy})`}
                            </span>
                          )}
                          <span className="mx-1 text-gray-300 dark:text-gray-600">-</span>
                          <span className={MARK[a.outcome]?.cls}>
                            {MARK[a.outcome]?.sym} {MARK[a.outcome]?.label}
                          </span>
                          {facts(a) && <span className="ml-1 tabular-nums text-gray-500 dark:text-gray-400">· {facts(a)}</span>}
                        </div>
                        {a.reason && <div className="text-gray-600 dark:text-gray-300 break-words">{a.reason}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
      </ol>
    </section>
  );
}
